import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation, useOutletContext } from 'react-router-dom'
import { ChevronDown, MoreHorizontal, Shield } from 'lucide-react'
import type { AppContext } from '../components/layout/AppLayout'
import { api, type ChatMessage as IChatMessage, type Model, type ContentPart } from '../lib/api'
import { ChatBubble, ChatInput, BottomSheet, ProviderBadge, CocoonBadge } from '../components/ui'
import { STATIC_MODELS } from '../lib/models-static'
import { chatStorage, type ChatSession } from '../lib/chat-storage'
import { haptics } from '../lib/haptics'
import { Input } from '../components/ui'
import { Search } from 'lucide-react'

interface Message extends IChatMessage {
  streaming?: boolean
  costHint?: string
  /** Thumbnail for display — not sent to API, only for UI */
  imageThumbnail?: string
}

/** Compress an image file to a base64 data URL (max 1024px, JPEG 0.82) */
async function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const MAX = 1024
      let { width, height } = img
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round((height * MAX) / width); width = MAX }
        else { width = Math.round((width * MAX) / height); height = MAX }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/jpeg', 0.82))
    }
    img.onerror = reject
    img.src = url
  })
}

function computeCostHint(
  userContent: string | ContentPart[],
  assistantContent: string,
  model: Model | undefined,
): string | undefined {
  if (!model) return undefined
  const textContent = typeof userContent === 'string'
    ? userContent
    : userContent.filter((p) => p.type === 'text').map((p) => (p as { type: 'text'; text: string }).text).join('')
  const inputTokens = Math.ceil(textContent.length / 4)
  const outputTokens = Math.ceil(assistantContent.length / 4)
  const cost =
    inputTokens * model.pricing.prompt_usd_per_token +
    outputTokens * model.pricing.completion_usd_per_token
  if (cost === 0) return undefined
  if (cost < 0.0001) return `~<$0.0001`
  return `~$${cost.toFixed(4)}`
}

const CATEGORY_ORDER = ['secure', 'flagship', 'everyday', 'coding', 'research', 'fast'] as const

export default function ChatSessionPage() {
  const { refreshBalance, balance, wallet } = useOutletContext<AppContext>()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  // Wallet required for chat — redirect to home if not connected
  useEffect(() => {
    if (!wallet) navigate('/', { replace: true })
  }, [wallet, navigate])

  // Initialize synchronously from localStorage — avoids flash of 'New chat' on first render
  const [session, setSession] = useState<ChatSession | null>(() => id ? chatStorage.get(id) : null)
  const [models, setModels] = useState<Model[]>(STATIC_MODELS)
  const [selectedModel, setSelectedModel] = useState(
    () => (id ? chatStorage.get(id)?.modelId : null) || (STATIC_MODELS[0]?.id ?? ''),
  )
  const [messages, setMessages] = useState<Message[]>(
    () => (id ? (chatStorage.get(id)?.messages as Message[] | undefined) : null) ?? [],
  )
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [modelPickerOpen, setModelPickerOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [clearConfirm, setClearConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [modelSearch, setModelSearch] = useState('')
  const [attachedImage, setAttachedImage] = useState<string | null>(null)
  const [attachedFile, setAttachedFile] = useState<{ name: string; size: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // Note: no bottomRef needed — scroll is driven via scrollContainerRef.scrollTop directly.
  // scrollContainerRef — the overflow-y:auto messages div.
  // Used for precise scroll-position tracking and programmatic scrolling.
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Ref for synchronous reads inside callbacks; state drives the button render.
  const isAtBottomRef = useRef(true)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const titleUpdatedRef = useRef(false)
  // Tracks streaming state across renders to detect streaming→complete transition.
  const wasStreamingRef = useRef(false)
  // Flag: auto-send the input once it's been set from navigation state
  const autoSendRef = useRef(false)

  // Load session on mount
  useEffect(() => {
    if (!id) {
      navigate('/chat', { replace: true })
      return
    }
    const s = chatStorage.get(id)
    if (!s) {
      navigate('/chat', { replace: true })
      return
    }
    // If the session already has a real title — don't overwrite it again
    titleUpdatedRef.current = s.title !== 'New Chat' && s.messages.length > 0
    setSession(s)
    setMessages(s.messages as Message[])
    setSelectedModel(s.modelId || (STATIC_MODELS[0]?.id ?? ''))

    // If navigated from compose (ChatListPage), pre-fill and auto-send the pending message
    const pending = (location.state as { pendingMessage?: string } | null)?.pendingMessage
    if (pending) {
      setInput(pending)
      autoSendRef.current = true
    }
  }, [id, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Try API refresh of models in background
  useEffect(() => {
    api.listModels()
      .then((list) => {
        setModels(list)
      })
      .catch(() => { /* keep static data */ })
  }, [])

  // Telegram BackButton
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window.Telegram?.WebApp as any)
    tg?.BackButton?.show()
    const handler = () => navigate('/chat')
    tg?.BackButton?.onClick(handler)
    return () => {
      tg?.BackButton?.hide()
      tg?.BackButton?.offClick(handler)
    }
  }, [navigate])

  // Keyboard avoidance (iOS Telegram)
  //
  // Problem: visualViewport.resize fires DURING keyboard animation with intermediate
  // vv.height values (e.g. 150px while keyboard is halfway up), while window.innerHeight
  // is still at full height → kbHeight spikes to ~700px → input jumps to the top.
  //
  // Fix: use Telegram's viewportChanged event which has isStateStable flag.
  // We only update padding when isStateStable=true (animation is fully complete).
  // This eliminates intermediate bad values. A CSS transition on paddingBottom
  // (set via inline style below) makes the snap feel smooth.
  //
  // Dev fallback: visualViewport with rAF debounce (good enough for local testing).
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window as any).Telegram?.WebApp

    const applyPadding = (kbH: number) => {
      if (!containerRef.current) return
      containerRef.current.style.paddingBottom = kbH > 20 ? `${kbH}px` : ''
      // Keep latest message visible after layout reflow caused by keyboard
      setTimeout(() => {
        if (isAtBottomRef.current && scrollContainerRef.current) {
          const el = scrollContainerRef.current
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
        }
      }, 0)
    }

    if (tg) {
      // Telegram fires viewportChanged; isStateStable=true means animation finished
      const handler = ({ isStateStable }: { isStateStable: boolean }) => {
        if (!isStateStable) return
        // viewportStableHeight = full height without keyboard
        // viewportHeight       = current visual height (shrinks when keyboard is open)
        const kbH = Math.max(0, tg.viewportStableHeight - tg.viewportHeight)
        applyPadding(kbH)
      }
      tg.onEvent('viewportChanged', handler)
      return () => tg.offEvent('viewportChanged', handler)
    }

    // Dev / non-Telegram fallback — rAF debounce avoids mid-animation spikes
    const vv = window.visualViewport
    if (!vv) return
    let raf = 0
    const update = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const kbH = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        applyPadding(kbH)
      })
    }
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      cancelAnimationFrame(raf)
    }
  }, [])

  // ── Scroll helpers ──────────────────────────────────────────────────────────

  /** Scroll the messages container to the bottom. */
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollContainerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
    isAtBottomRef.current = true
    setIsAtBottom(true)
  }, [])

  /** onScroll handler — updates isAtBottom when user manually scrolls. */
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    // 50px threshold: small discrepancies after layout reflows should still count as "at bottom"
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
    isAtBottomRef.current = atBottom
    setIsAtBottom(atBottom)
  }, [])

  // ── Auto-scroll effect ───────────────────────────────────────────────────────
  // Three cases:
  //   1. New message added         → always scroll (user just sent or first response token arrived)
  //   2. Token during streaming    → scroll only if already at bottom (user scrolled up = don't interrupt)
  //   3. Streaming just completed  → re-snap to bottom to absorb any height change from markdown render
  const messageCountRef = useRef(0)
  useEffect(() => {
    const count = messages.length
    const lastMsg = messages[messages.length - 1]
    const isLastStreaming = lastMsg?.streaming === true

    if (count !== messageCountRef.current) {
      // Case 1: new message — always scroll to bottom
      messageCountRef.current = count
      scrollToBottom('smooth')
    } else if (isLastStreaming) {
      // Case 2: token during streaming — only if at bottom
      if (isAtBottomRef.current) {
        const el = scrollContainerRef.current
        if (el) el.scrollTop = el.scrollHeight
      }
    } else if (wasStreamingRef.current && !isLastStreaming && isAtBottomRef.current) {
      // Case 3: streaming just ended — re-snap so markdown height change doesn't leave a gap
      scrollToBottom('instant')
    }

    wasStreamingRef.current = isLastStreaming
  }, [messages, scrollToBottom])

  const currentModel = models.find((m) => m.id === selectedModel)

  const persistSession = useCallback(
    (newMessages: Message[], sessionOverride?: ChatSession) => {
      const s = sessionOverride ?? session
      if (!s) return
      const updated: ChatSession = {
        ...s,
        modelId: selectedModel,
        modelName: currentModel?.name ?? s.modelName,
        messages: newMessages.map(({ role, content }) => ({ role, content: content })),
        updatedAt: Date.now(),
      }
      chatStorage.save(updated)
      setSession(updated)
    },
    [session, selectedModel, currentModel],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const send = useCallback(async () => {
    const text = input.trim()
    if ((!text && !attachedImage) || isStreaming || !selectedModel) return

    setInput('')
    setError(null)

    // Build content — plain string or vision array if image attached
    const userContent: IChatMessage['content'] = attachedImage
      ? ([
          { type: 'image_url', image_url: { url: attachedImage, detail: 'auto' } },
          ...(text ? [{ type: 'text', text }] : []),
        ] as ContentPart[])
      : text

    const imageThumbnail = attachedImage ?? undefined
    setAttachedImage(null)

    const userMsg: Message = { role: 'user', content: userContent, imageThumbnail }
    const assistantPlaceholder: Message = { role: 'assistant', content: '', streaming: true }

    const nextMessages = [...messages, userMsg, assistantPlaceholder]
    setMessages(nextMessages)
    setIsStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller
    let full = ''

    try {
      const history = [...messages, userMsg].map(({ role, content }) => ({ role, content }))

      for await (const chunk of api.streamChat(selectedModel, history, controller.signal)) {
        full += chunk
        setMessages((prev) => [
          ...prev.slice(0, -1),
          { role: 'assistant', content: full, streaming: true },
        ])
      }

      const costHint = computeCostHint(text, full, currentModel)
      const completedAssistant: Message = { role: 'assistant', content: full, streaming: false, costHint }
      const finalMessages = [...messages, userMsg, completedAssistant]

      setMessages(finalMessages)
      persistSession(finalMessages)

      // Generate AI title from first user message (once, async — does not block UI)
      if (!titleUpdatedRef.current && id) {
        titleUpdatedRef.current = true
        const titleText = text || 'Attached image'
        const titleModel = selectedModel
        const titleId = id
        void api.generateTitle(titleModel, titleText)
          .then((title) => {
            if (!title) return
            chatStorage.updateTitle(titleId, title)
            const updated = chatStorage.get(titleId)
            if (updated) setSession(updated)
          })
          .catch(() => {
            // Fallback: use raw text slice
            chatStorage.updateTitle(titleId, titleText.slice(0, 50).trim().replace(/\n+/g, ' '))
            const updated = chatStorage.get(titleId)
            if (updated) setSession(updated)
          })
      }

      refreshBalance()
    } catch (err: unknown) {
      // User stopped — keep user message visible; save partial assistant content if any
      if (err instanceof DOMException && err.name === 'AbortError') {
        if (full.length > 0) {
          const partial: Message = { role: 'assistant', content: full, streaming: false }
          const finalMessages = [...messages, userMsg, partial]
          setMessages(finalMessages)
          persistSession(finalMessages)
        } else {
          // No tokens received yet — remove only the empty assistant placeholder,
          // but keep the user message so they can retry.
          setMessages((prev) => prev.slice(0, -1))
        }
        return
      }
      haptics.notification('error')
      const raw = err instanceof Error ? err.message : 'Something went wrong'
      let msg = raw
      if (raw.includes('401') || raw.toLowerCase().includes('unauthorized')) {
        msg = 'Invalid API key. Check Home → Integration.'
      } else if (raw.includes('402') || raw.toLowerCase().includes('insufficient') || raw.toLowerCase().includes('payment')) {
        msg = 'Not enough credits. Top up in Deposit.'
      } else if (raw.includes('429') || raw.toLowerCase().includes('rate')) {
        msg = 'Rate limit reached. Wait a moment and retry.'
      } else if (raw.includes('503') || raw.includes('502')) {
        msg = 'Model unavailable. Try another model.'
      }
      setError(msg)
      setInput(text) // restore input so Retry can re-send
      setMessages((prev) => prev.slice(0, -2)) // remove user + failed assistant
    } finally {
      setIsStreaming(false)
    }
  }, [input, isStreaming, selectedModel, messages, currentModel, persistSession, refreshBalance, id])

  // Auto-send once input is set from navigation state (compose flow from ChatListPage)
  useEffect(() => {
    if (!autoSendRef.current || !input || isStreaming) return
    autoSendRef.current = false
    void send()
  }, [input, send, isStreaming])

  const startEditTitle = () => {
    setTitleDraft(session?.title || '')
    setEditingTitle(true)
    // Focus after state update
    setTimeout(() => titleInputRef.current?.focus(), 50)
  }

  const saveTitle = () => {
    const trimmed = titleDraft.trim()
    if (trimmed && id) {
      chatStorage.updateTitle(id, trimmed)
      const updated = chatStorage.get(id)
      if (updated) setSession(updated)
    }
    setEditingTitle(false)
  }

  const handleClearConversation = () => {
    if (!session || !id) return
    haptics.notification('warning')
    const cleared: ChatSession = { ...session, messages: [] }
    chatStorage.save(cleared)
    setSession(cleared)
    setMessages([])
    setMoreOpen(false)
    setClearConfirm(false)
    titleUpdatedRef.current = false
  }

  const handleDeleteChat = () => {
    if (!id) return
    haptics.notification('warning')
    chatStorage.delete(id)
    navigate('/chat', { replace: true })
  }

  // Group models for picker
  const filteredModels = modelSearch
    ? models.filter(
        (m) =>
          m.name.toLowerCase().includes(modelSearch.toLowerCase()) ||
          m.id.toLowerCase().includes(modelSearch.toLowerCase()),
      )
    : models

  const groupedModels = new Map<string, Model[]>()
  for (const cat of CATEGORY_ORDER) groupedModels.set(cat, [])
  for (const m of filteredModels) {
    groupedModels.get(m.category ?? 'everyday')?.push(m)
  }

  return (
    <div ref={containerRef} className="flex flex-col flex-1 min-h-0" style={{ transition: 'padding-bottom 150ms ease-out' }}>
      {/* Chat header — 3 columns: title | model chip | more */}
      <div
        className="flex items-center px-sp-2 shrink-0 relative"
        style={{
          borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
          height: selectedModel.startsWith('cocoon/') ? '58px' : '48px',
          paddingBottom: selectedModel.startsWith('cocoon/') ? '12px' : undefined,
        }}
      >
        {/* Left: session title — tap to edit */}
        <div className="flex items-center min-w-0" style={{ flex: '1 1 0' }}>
          {editingTitle ? (
            <input
              ref={titleInputRef}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); saveTitle() }
                if (e.key === 'Escape') setEditingTitle(false)
              }}
              className="text-[14px] font-medium bg-transparent outline-none w-full pl-sp-2"
              style={{
                color: 'var(--tg-theme-text-color)',
                caretColor: 'var(--tg-theme-accent-text-color)',
              }}
              maxLength={60}
            />
          ) : (
            <button
              onClick={() => { haptics.impact('light'); startEditTitle() }}
              className="text-[14px] font-medium truncate pl-sp-2 active:opacity-70 transition-opacity text-left min-w-0 w-full"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              {session?.title || 'New chat'}
            </button>
          )}
        </div>

        {/* Center: model chip — tappable */}
        <button
          onClick={() => {
            haptics.impact('light')
            setModelPickerOpen(true)
          }}
          className="flex items-center gap-1 px-3 h-9 rounded-full active:opacity-70 transition-opacity shrink-0"
          style={selectedModel.startsWith('cocoon/')
            ? {
                background: 'linear-gradient(110deg, rgba(160,98,255,0.15) 0%, rgba(48,136,255,0.15) 50%, rgba(97,216,255,0.15) 100%)',
                border: '1px solid rgba(160,98,255,0.35)',
              }
            : { backgroundColor: 'var(--tg-theme-secondary-bg-color)' }
          }
        >
          {selectedModel.startsWith('cocoon/') && (
            <Shield size={12} style={{ color: '#A062FF', flexShrink: 0 }} />
          )}
          <span
            className="text-[13px] font-medium truncate max-w-[120px]"
            style={{ color: 'var(--tg-theme-text-color)' }}
          >
            {currentModel?.name ?? 'Select model'}
          </span>
          <ChevronDown
            size={14}
            className="shrink-0"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          />
        </button>
        {selectedModel.startsWith('cocoon/') && (
          <p className="absolute left-1/2 -translate-x-1/2 text-[11px]" style={{ color: 'rgba(160, 98, 255, 0.7)', top: '44px' }}>
            Secure · slower response
          </p>
        )}

        {/* Right: more button */}
        <div className="flex items-center justify-end" style={{ flex: '1 1 0' }}>
          <button
            onClick={() => {
              haptics.impact('light')
              setMoreOpen(true)
              setClearConfirm(false)
              setDeleteConfirm(false)
            }}
            className="w-11 h-11 flex items-center justify-center active:opacity-70 transition-opacity"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            <MoreHorizontal size={20} />
          </button>
        </div>
      </div>

      {/* Messages wrapper — relative so the scroll-to-bottom button can be
          absolutely positioned within this area, floating above the messages
          and below the header, regardless of the input bar height. */}
      <div className="flex-1 min-h-0 relative">
        {/* Scrollable messages area */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="absolute inset-0 flex flex-col scroll-area px-sp-3 py-sp-2"
        >
          {messages.length === 0 && (
            <div
              className="flex flex-col items-center justify-center flex-1 gap-sp-2 opacity-40"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              <span className="text-[40px]">✦</span>
              <span className="text-[13px] text-center">Ask anything. Pay only for what you use.</span>
            </div>
          )}
          {messages.map((msg, i) => (
            <ChatBubble
              key={i}
              role={msg.role as 'user' | 'assistant'}
              content={typeof msg.content === 'string'
                ? msg.content
                : msg.content.filter((p) => p.type === 'text').map((p) => (p as { type: 'text'; text: string }).text).join('')
              }
              streaming={msg.streaming}
              model={msg.role === 'assistant' ? currentModel?.name : undefined}
              costHint={msg.costHint}
              imageThumbnail={msg.imageThumbnail}
              isSecure={selectedModel.startsWith('cocoon/')}
            />
          ))}

          {/* Error with retry + contextual nav */}
          {error && (
            <div className="flex flex-col items-center gap-1 py-sp-2">
              <p className="text-[13px] text-center" style={{ color: 'var(--tg-theme-destructive-text-color)' }}>
                {error}
              </p>
              <div className="flex items-center gap-sp-3">
                <button
                  onClick={() => { setError(null); void send() }}
                  className="text-[13px] active:opacity-70 transition-opacity"
                  style={{ color: 'var(--tg-theme-accent-text-color)' }}
                >
                  Retry
                </button>
                {(error.includes('API key') || error.includes('Invalid')) && (
                  <button
                    onClick={() => navigate('/')}
                    className="text-[13px] active:opacity-70 transition-opacity"
                    style={{ color: 'var(--tg-theme-accent-text-color)' }}
                  >
                    Home →
                  </button>
                )}
                {(error.includes('credits') || error.includes('Top up') || error.includes('balance')) && (
                  <button
                    onClick={() => navigate('/deposit')}
                    className="text-[13px] active:opacity-70 transition-opacity"
                    style={{ color: 'var(--tg-theme-accent-text-color)' }}
                  >
                    Deposit →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Scroll-to-bottom button — appears when user scrolls up to read history.
            Positioned at bottom-right of the messages area, above the input bar.
            Uses absolute positioning inside the relative wrapper so it overlays
            the scroll content without affecting layout flow. */}
        {!isAtBottom && (
          <button
            onClick={() => {
              haptics.impact('light')
              scrollToBottom('instant')
            }}
            className="absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center active:opacity-70 transition-opacity z-10"
            style={{
              backgroundColor: 'var(--tg-theme-bg-color)',
              border: '1px solid var(--tg-theme-secondary-bg-color)',
              color: 'var(--tg-theme-hint-color)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
            }}
          >
            <ChevronDown size={18} />
          </button>
        )}
      </div>

      {/* Low balance nudge — subtle, not alarming */}
      {(balance?.creditsUsd ?? 1) < 0.10 && !isStreaming && (
        <div
          className="shrink-0 flex items-center justify-between px-sp-4 py-2 gap-sp-3"
          style={{ borderTop: '1px solid var(--tg-theme-secondary-bg-color)' }}
        >
          <span className="text-[12px]" style={{ color: 'var(--tg-theme-hint-color)' }}>
            Balance: ${(balance?.creditsUsd ?? 0).toFixed(3)}
          </span>
          <button
            onClick={() => { haptics.impact('medium'); navigate('/deposit') }}
            className="text-[12px] font-medium shrink-0 active:opacity-70 transition-opacity"
            style={{ color: 'var(--tg-theme-accent-text-color)' }}
          >
            Top up
          </button>
        </div>
      )}

      {/* Hidden file input — no accept filter so iOS shows Photo Library / Take Photo / Choose File natively */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (!file) return
          e.target.value = ''
          haptics.impact('light')
          if (file.type.startsWith('image/')) {
            try {
              const compressed = await compressImage(file)
              setAttachedImage(compressed)
              setAttachedFile(null)
            } catch {
              haptics.notification('error')
            }
          } else {
            // Non-image file — show name preview, document sending coming soon
            const kb = (file.size / 1024).toFixed(0)
            setAttachedFile({ name: file.name, size: `${kb} KB` })
            setAttachedImage(null)
          }
        }}
      />

      {/* Input bar — "+" directly triggers native iOS picker (no custom bottom sheet needed) */}
      <ChatInput
        value={input}
        onChange={setInput}
        onSend={send}
        onStop={stop}
        disabled={isStreaming}
        autoFocus
        attachedImage={attachedImage}
        attachedFile={attachedFile}
        onRemoveImage={() => { setAttachedImage(null); setAttachedFile(null) }}
        onAttach={() => { haptics.impact('light'); fileInputRef.current?.click() }}
      />

      {/* More options bottom sheet */}
      <BottomSheet
        open={moreOpen}
        onClose={() => { setMoreOpen(false); setClearConfirm(false); setDeleteConfirm(false) }}
        title="Options"
      >
        <div className="pb-sp-6">
          {!clearConfirm && !deleteConfirm ? (
            <>
              <button
                onClick={() => {
                  haptics.impact('light')
                  setMoreOpen(false)
                  setTimeout(() => setModelPickerOpen(true), 300)
                }}
                className="w-full flex flex-col justify-center px-sp-4 py-sp-3 gap-0.5 min-h-[56px] active:opacity-70 transition-opacity"
                style={{ borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' }}
              >
                <span className="text-[17px]" style={{ color: 'var(--tg-theme-text-color)' }}>
                  Change model
                </span>
                {currentModel && (
                  <span className="text-[13px]" style={{ color: 'var(--tg-theme-hint-color)' }}>
                    {currentModel.name}
                  </span>
                )}
              </button>

              <button
                onClick={() => { haptics.impact('light'); setClearConfirm(true) }}
                className="w-full flex items-center h-14 px-sp-4 active:opacity-70 transition-opacity"
                style={{ borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' }}
              >
                <span className="text-[17px]" style={{ color: 'var(--tg-theme-text-color)' }}>
                  Clear conversation
                </span>
              </button>

              <div className="h-sp-2" />

              <button
                onClick={() => { haptics.impact('heavy'); setDeleteConfirm(true) }}
                className="w-full flex items-center h-14 px-sp-4 active:opacity-70 transition-opacity"
              >
                <span className="text-[17px]" style={{ color: 'var(--tg-theme-destructive-text-color)' }}>
                  Delete chat
                </span>
              </button>
            </>
          ) : clearConfirm ? (
            <div className="px-sp-4 flex flex-col gap-sp-3">
              <p className="text-[15px] text-center" style={{ color: 'var(--tg-theme-text-color)' }}>
                Clear all messages? This cannot be undone.
              </p>
              <button
                onClick={handleClearConversation}
                className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-destructive-text-color)', color: '#ffffff' }}
              >
                Clear
              </button>
              <button
                onClick={() => { haptics.impact('light'); setClearConfirm(false) }}
                className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)', color: 'var(--tg-theme-text-color)' }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="px-sp-4 flex flex-col gap-sp-3">
              <p className="text-[15px] text-center" style={{ color: 'var(--tg-theme-text-color)' }}>
                Delete this chat? All messages will be lost.
              </p>
              <button
                onClick={handleDeleteChat}
                className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-destructive-text-color)', color: '#ffffff' }}
              >
                Delete
              </button>
              <button
                onClick={() => { haptics.impact('light'); setDeleteConfirm(false) }}
                className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
                style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)', color: 'var(--tg-theme-text-color)' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Model picker bottom sheet */}
      <BottomSheet
        open={modelPickerOpen}
        onClose={() => { setModelPickerOpen(false); setModelSearch('') }}
        title="Select model"
        maxHeight="85vh"
      >
        <div className="pb-sp-6">
          {/* Search */}
          <div className="px-sp-4 pb-sp-3">
            <Input
              leftIcon={<Search size={16} />}
              placeholder="Search models..."
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
            />
          </div>

          {modelSearch ? (
            filteredModels.map((m) => (
              <ModelPickerRow
                key={m.id}
                model={m}
                selected={m.id === selectedModel}
                onSelect={() => {
                  haptics.selection()
                  setSelectedModel(m.id)
                  if (session) {
                    const updated = { ...session, modelId: m.id, modelName: m.name }
                    chatStorage.save(updated)
                    setSession(updated)
                  }
                  setModelPickerOpen(false)
                  setModelSearch('')
                }}
              />
            ))
          ) : (
            CATEGORY_ORDER.map((cat) => {
              const group = groupedModels.get(cat) ?? []
              if (group.length === 0) return null
              const LABELS: Record<string, string> = {
                secure: 'Cocoon', flagship: 'Flagship', everyday: 'General', coding: 'Coding', research: 'Research', fast: 'Fast'
              }
              return (
                <div key={cat}>
                  <div
                    className="px-sp-4 py-sp-2"
                    style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
                  >
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--tg-theme-hint-color)' }}
                    >
                      {LABELS[cat]}
                    </span>
                  </div>
                  {group.map((m) => (
                    <ModelPickerRow
                      key={m.id}
                      model={m}
                      selected={m.id === selectedModel}
                      onSelect={() => {
                        haptics.selection()
                        setSelectedModel(m.id)
                        if (session) {
                          const updated = { ...session, modelId: m.id, modelName: m.name }
                          chatStorage.save(updated)
                          setSession(updated)
                        }
                        setModelPickerOpen(false)
                        setModelSearch('')
                      }}
                    />
                  ))}
                </div>
              )
            })
          )}
        </div>
      </BottomSheet>
    </div>
  )
}

function ModelPickerRow({
  model,
  selected,
  onSelect,
}: {
  model: Model
  selected: boolean
  onSelect: () => void
}) {
  const isMaintenance = model.status === 'maintenance'
  return (
    <button
      onClick={isMaintenance ? undefined : onSelect}
      disabled={isMaintenance}
      className={`w-full text-left px-sp-4 py-sp-3 flex items-center justify-between transition-opacity ${
        isMaintenance ? 'opacity-50' : 'active:opacity-70'
      }`}
      style={{
        backgroundColor: selected && !isMaintenance ? 'var(--tg-theme-secondary-bg-color)' : 'transparent',
        borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
      }}
    >
      <div className="flex-1 min-w-0 flex items-center gap-sp-2">
        {selected && !isMaintenance && (
          <span className="text-[15px]" style={{ color: 'var(--tg-theme-button-color)' }}>
            ✓
          </span>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-sp-1 flex-wrap">
            <span
              className="text-[15px] font-medium"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              {model.name}
            </span>
            {model.id.startsWith('cocoon/')
              ? <CocoonBadge />
              : <ProviderBadge modelId={model.id} size="sm" />
            }
          </div>
        </div>
      </div>
      {isMaintenance ? (
        <span
          className="text-[11px] font-semibold shrink-0 ml-sp-3 px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: 'rgba(255, 159, 10, 0.15)',
            color: 'rgb(255, 159, 10)',
          }}
        >
          Maintenance
        </span>
      ) : (
        <span
          className="text-[13px] font-mono shrink-0 ml-sp-3 tabular-nums"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          ${(model.pricing.prompt_usd_per_token * 1_000_000).toFixed(2)}/M
        </span>
      )}
    </button>
  )
}
